import { Vec3 } from '../math/vec3';
import { Mat4 } from '../math/mat4';

export class Camera {
  position = new Vec3(0, 2, 6);
  target = new Vec3(0, 0, 0);
  up = new Vec3(0, 1, 0);
  fov = 70; // degrees, vertical
  near = 0.1;
  far = 400;
  aspect = 16 / 9;
  readonly view = new Mat4();
  readonly proj = new Mat4();
  readonly viewProj = new Mat4();
  readonly invViewProj = new Mat4();
  /** 6 frustum planes as [nx, ny, nz, d] with n·p + d >= 0 inside. */
  readonly planes = new Float32Array(24);

  update(): void {
    this.view.lookAt(this.position, this.target, this.up);
    this.proj.perspective((this.fov * Math.PI) / 180, this.aspect, this.near, this.far);
    this.viewProj.multiplyMatrices(this.proj, this.view);
    this.invViewProj.copy(this.viewProj).invert();
    this.extractPlanes();
  }

  private extractPlanes(): void {
    const m = this.viewProj.e, p = this.planes;
    const rows = [
      [m[3] + m[0], m[7] + m[4], m[11] + m[8], m[15] + m[12]],
      [m[3] - m[0], m[7] - m[4], m[11] - m[8], m[15] - m[12]],
      [m[3] + m[1], m[7] + m[5], m[11] + m[9], m[15] + m[13]],
      [m[3] - m[1], m[7] - m[5], m[11] - m[9], m[15] - m[13]],
      [m[3] + m[2], m[7] + m[6], m[11] + m[10], m[15] + m[14]],
      [m[3] - m[2], m[7] - m[6], m[11] - m[10], m[15] - m[14]],
    ];
    for (let i = 0; i < 6; i++) {
      const [a, b, c, d] = rows[i];
      const l = Math.hypot(a, b, c) || 1;
      p[i * 4] = a / l; p[i * 4 + 1] = b / l; p[i * 4 + 2] = c / l; p[i * 4 + 3] = d / l;
    }
  }

  /** Sphere-vs-frustum test. */
  sphereVisible(cx: number, cy: number, cz: number, r: number): boolean {
    const p = this.planes;
    for (let i = 0; i < 6; i++) {
      if (p[i * 4] * cx + p[i * 4 + 1] * cy + p[i * 4 + 2] * cz + p[i * 4 + 3] < -r) return false;
    }
    return true;
  }

  forward(out = new Vec3()): Vec3 { return out.copy(this.target).sub(this.position).normalize(); }
}
